export const generateCurlSnippet = (api, paramValues, securitySchemes, activeScheme, showAuthorization) => {
  const { baseUrl, path, method, parameters, requestBody } = api;
  let populatedPath = path;
  let queryParams = [];

  parameters.forEach(p => {
    const value = paramValues[p.name];
    if (value) {
      if (p.in === 'path') {
        populatedPath = populatedPath.replace(`{${p.name}}`, encodeURIComponent(value));
      } else if (p.in === 'query') {
        queryParams.push(`${encodeURIComponent(p.name)}=${encodeURIComponent(value)}`);
      }
    }
  });

  if (showAuthorization && activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'query') {
    const schemeDetails = securitySchemes[activeScheme];
    const value = paramValues[schemeDetails.name];
    if (value) {
      queryParams.push(`${encodeURIComponent(schemeDetails.name)}=${encodeURIComponent(value)}`);
    }
  }

  if (queryParams.length > 0) {
    populatedPath += `?${queryParams.join('&')}`;
  }
  
  let curl = `curl -X ${method.toUpperCase()} "${baseUrl}${populatedPath}"`;

  const headers = parameters.filter(p => p.in === 'header');
  headers.forEach(h => {
    const value = paramValues[h.name] || `{${h.name}_value}`;
    curl += ` \\\n     -H "${h.name}: ${value}"`;
  });

  if (showAuthorization && activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
    const schemeDetails = securitySchemes[activeScheme];
    let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
    if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
      token = `Bearer ${token}`;
    }
    const authHeader = `${schemeDetails.name}: ${token}`;
    curl += ` \\\n     -H "${authHeader}"`;
  }

  if (requestBody) {
    curl += ` \\\n     -H "Content-Type: application/json"`;
    let bodyData = { ...requestBody.example };
    parameters.filter(p => p.in === 'body').forEach(p => {
        if(paramValues.hasOwnProperty(p.name)) {
            bodyData[p.name] = paramValues[p.name];
        }
    });
    curl += ` \\\n     -d '${JSON.stringify(bodyData, null, 2)}'`;
  }

  return curl;
};

export const generateJavascriptSnippet = (api, paramValues, securitySchemes, activeScheme) => {
    const { baseUrl, path, method, parameters, requestBody } = api;
    let populatedPath = path;
    parameters.filter(p => p.in === 'path').forEach(p => {
      const value = paramValues[p.name];
      if(value) {
        populatedPath = populatedPath.replace(`{${p.name}}`, encodeURIComponent(value));
      }
    });

    let js = `import axios from 'axios';\n\n`;
    js += `axios({\n`;
    js += `  method: '${method.toLowerCase()}',\n`;
    js += `  url: '${baseUrl}${populatedPath}',\n`;

    const queryParams = parameters.filter(p => p.in === 'query');
    const queryParamsObj = {};
    queryParams.forEach(p => {
        queryParamsObj[p.name] = paramValues[p.name] || `your_${p.name}_value`;
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'query') {
        const schemeDetails = securitySchemes[activeScheme];
        const value = paramValues[schemeDetails.name];
        if (value) {
          queryParamsObj[schemeDetails.name] = value;
        }
    }

    if (Object.keys(queryParamsObj).length > 0) {
        js += `  params: {\n`;
        for (const key in queryParamsObj) {
            js += `    ${key}: '${queryParamsObj[key]}',\n`;
        }
        js += `  },\n`;
    }

    let jsHeaders = "  headers: {\n";
    let hasHeaders = false;

    const headers = parameters.filter(p => p.in === 'header');
    if (headers.length > 0) {
        hasHeaders = true;
        headers.forEach(h => {
            jsHeaders += `    '${h.name}': '${paramValues[h.name] || `{${h.name}_value}`}',\n`;
        });
    }

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        hasHeaders = true;
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        jsHeaders += `    '${schemeDetails.name}': '${token}',\n`;
    }

    if (hasHeaders) {
        js += jsHeaders + "  },\n";
    }

    if (requestBody) {
        let bodyData = { ...requestBody.example };
        parameters.filter(p => p.in === 'body').forEach(p => {
            if(paramValues.hasOwnProperty(p.name)) {
                bodyData[p.name] = paramValues[p.name];
            }
        });
        js += `  data: ${JSON.stringify(bodyData, null, 2)}\n`;
    }
  
    js += `})\n.then(response => {\n  console.log(response.data);\n})\n.catch(error => {\n  console.error(error);\n});`;
    return js;
};

export const generatePythonSnippet = (api, paramValues, securitySchemes, activeScheme) => {
    const { baseUrl, path, method, parameters, requestBody } = api;
    let populatedPath = path;
    parameters.filter(p => p.in === 'path').forEach(p => {
        const value = paramValues[p.name];
        if (value) {
            populatedPath = populatedPath.replace(`{${p.name}}`, encodeURIComponent(value));
        }
    });

    let py = `import requests\nimport json\n\n`;
    py += `url = "${baseUrl}${populatedPath}"\n\n`;

    const queryParams = parameters.filter(p => p.in === 'query');
    const queryParamsObj = {};
    queryParams.forEach(p => {
        queryParamsObj[p.name] = paramValues[p.name] || `your_${p.name}_value`;
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'query') {
        const schemeDetails = securitySchemes[activeScheme];
        const value = paramValues[schemeDetails.name];
        if (value) {
            queryParamsObj[schemeDetails.name] = value;
        }
    }

    if (Object.keys(queryParamsObj).length > 0) {
        py += `params = {\n`;
        for (const key in queryParamsObj) {
            py += `    "${key}": "${queryParamsObj[key]}",\n`;
        }
        py += `}\n\n`;
    }

    const headers = parameters.filter(p => p.in === 'header');
    let pyHeaders = "headers = {\n";
    let hasHeaders = false;

    if (headers.length > 0) {
        hasHeaders = true;
        headers.forEach(h => {
            pyHeaders += `    "${h.name}": "${paramValues[h.name] || `{${h.name}_value}`}",\n`;
        });
    }

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        hasHeaders = true;
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        pyHeaders += `    "${schemeDetails.name}": "${token}",\n`;
    }

    if (requestBody) {
        hasHeaders = true;
        pyHeaders += `    "Content-Type": "application/json"\n`;
        let bodyData = { ...requestBody.example };
        parameters.filter(p => p.in === 'body').forEach(p => {
            if(paramValues.hasOwnProperty(p.name)) {
                bodyData[p.name] = paramValues[p.name];
            }
        });
        py += `payload = json.dumps(${JSON.stringify(bodyData, null, 2)})\n\n`;
    }
    
    if (hasHeaders) {
        py += pyHeaders + "}\n\n";
    }

    let reqLine = `response = requests.request("${method.toUpperCase()}", url`;

    if (queryParams.length > 0) reqLine += ", params=params";
    if (hasHeaders) reqLine += ", headers=headers";
    if (requestBody) reqLine += ", data=payload";
    reqLine += ")\n\n";
    py += reqLine;

    py += `print(response.text)`;
    return py;
};

export const generateRubySnippet = (api, paramValues, securitySchemes, activeScheme) => {
    const { baseUrl, path, method, parameters, requestBody } = api;
    let ruby = `require 'uri'\nrequire 'net/http'\nrequire 'json'\n\n`;
    
    let urlString = `${baseUrl}${path}`;
    const queryParams = parameters.filter(p => p.in === 'query');
    const queryParts = [];
    queryParams.forEach(p => {
        queryParts.push(`${p.name}=${paramValues[p.name] || `your_${p.name}_value`}`);
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'query') {
        const schemeDetails = securitySchemes[activeScheme];
        const value = paramValues[schemeDetails.name];
        if (value) {
            queryParts.push(`${schemeDetails.name}=${value}`);
        }
    }

    if (queryParts.length > 0) {
        urlString += `?${queryParts.join('&')}`;
    }

    ruby += `url = URI("${urlString}")\n\n`;
    ruby += `http = Net::HTTP.new(url.host, url.port)\n`;
    ruby += `http.use_ssl = true\n\n`;

    ruby += `request = Net::HTTP::${method.charAt(0).toUpperCase() + method.slice(1)}.new(url)\n`;
    
    const headers = parameters.filter(p => p.in === 'header');
    headers.forEach(h => {
        ruby += `request["${h.name}"] = '{${h.name}_value}'\n`;
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        ruby += `request["${schemeDetails.name}"] = '${token}'\n`;
    }
    
    if (requestBody) {
        ruby += `request["Content-Type"] = 'application/json'\n`;
        ruby += `request.body = JSON.dump(${JSON.stringify(requestBody.example || {}, null, 2)})\n`;
    }
    
    ruby += `\nresponse = http.request(request)\n`;
    ruby += `puts response.read_body`;

    return ruby;
};

export const generateCSnippet = (api, paramValues, securitySchemes, activeScheme) => {
  const { baseUrl, path, requestBody } = api;
  let c = `#include <stdio.h>\n#include <curl/curl.h>\n\n`;
  c += `int main(void) {\n`;
  c += `  CURL *curl;\n`;
  c += `  CURLcode res;\n\n`;
  c += `  curl_global_init(CURL_GLOBAL_ALL);\n`;
  c += `  curl = curl_easy_init();\n`;
  c += `  if(curl) {\n`;
  c += `    struct curl_slist *headers = NULL;\n`;
  c += `    headers = curl_slist_append(headers, "Accept: application/json");\n`;

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        c += `    headers = curl_slist_append(headers, "${schemeDetails.name}: ${token}");\n`;
    }
  
  if (requestBody) {
    c += `    headers = curl_slist_append(headers, "Content-Type: application/json");\n`;
    c += `    const char *data = "${JSON.stringify(JSON.stringify(requestBody.example || {}))}";\n`;
  }
  
  c += `\n    curl_easy_setopt(curl, CURLOPT_URL, "${baseUrl}${path}");\n`;
  c += `    curl_easy_setopt(curl, CURLOPT_HTTPHEADER, headers);\n`;

  if (requestBody) {
    c += `    curl_easy_setopt(curl, CURLOPT_POSTFIELDS, data);\n`;
  }

  c += `\n    res = curl_easy_perform(curl);\n`;
  c += `    if(res != CURLE_OK)\n`;
  c += `      fprintf(stderr, "curl_easy_perform() failed: %s\\n", curl_easy_strerror(res));\n\n`;
  c += `    curl_easy_cleanup(curl);\n`;
  c += `  }\n`;
  c += `  curl_global_cleanup();\n`;
  c += `  return 0;\n`;
  c += `}`;
  return c;
};

export const generateCppSnippet = (api, paramValues, securitySchemes, activeScheme) => {
    const { baseUrl, path, method, requestBody } = api;
    let cpp = `#include <iostream>\n#include <cpr/cpr.h>\n\n`;
    cpp += `int main(int argc, char** argv) {\n`;
    
    let headers = `{"Accept", "application/json"}`;
    if (requestBody) {
        headers += `, {"Content-Type", "application/json"}`;
    }

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        headers += `, {"${schemeDetails.name}", "${token}"}`;
    }

    let reqLine = `    auto r = cpr::${method.charAt(0).toUpperCase() + method.slice(1).toLowerCase()}(cpr::Url{"${baseUrl}${path}"}`;

    if (requestBody) {
        reqLine += `,\n                      cpr::Body{R"(${JSON.stringify(requestBody.example || {})})"},`;
    }
    
    reqLine += `\n                      cpr::Header{${headers}}`;
    reqLine += `);\n`;

    cpp += reqLine;
    cpp += `    std::cout << r.status_code << std::endl;\n`;
    cpp += `    std::cout << r.text << std::endl;\n\n`;
    cpp += `    return 0;\n`;
    cpp += `}`;
    return cpp;
};

export const generateJavaSnippet = (api, paramValues, securitySchemes, activeScheme) => {
    const { baseUrl, path, method, requestBody } = api;
    let java = `import okhttp3.*;\n\n`;
    java += `import java.io.IOException;\n\n`;
    java += `public class Main {\n`;
    java += `    public static void main(String[] args) throws IOException {\n`;
    java += `        OkHttpClient client = new OkHttpClient();\n\n`;
    
    if (requestBody) {
        java += `        MediaType mediaType = MediaType.parse("application/json");\n`;
        java += `        RequestBody body = RequestBody.create(mediaType, "${JSON.stringify(JSON.stringify(requestBody.example || {}))}");\n`;
    }

    java += `        Request.Builder requestBuilder = new Request.Builder()\n`;
    java += `                .url("${baseUrl}${path}")\n`;
    java += `                .${method.toLowerCase()}(${requestBody ? 'body' : 'null'})\n`;
    java += `                .addHeader("Accept", "application/json");\n`;

    if (requestBody) {
        java += `        requestBuilder.addHeader("Content-Type", "application/json");\n`;
    }

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        java += `        requestBuilder.addHeader("${schemeDetails.name}", "${token}");\n`;
    }

    java += `\n        Request request = requestBuilder.build();\n\n`;
    java += `        Response response = client.newCall(request).execute();\n`;
    java += `        System.out.println(response.body().string());\n`;
    java += `    }\n`;
    java += `}`;
    return java;
};

export const generateShellSnippet = (api, paramValues, securitySchemes, activeScheme) => {
  return generateCurlSnippet(api, paramValues, securitySchemes, activeScheme);
};

export const generateNodeSnippet = (api, paramValues, securitySchemes, activeScheme) => {
  return generateJavascriptSnippet(api, paramValues, securitySchemes, activeScheme);
};

export const generatePHPSnippet = (api, paramValues, securitySchemes, activeScheme) => {
    const { baseUrl, path, method, parameters, requestBody } = api;
    let populatedPath = path;
    parameters.filter(p => p.in === 'path').forEach(p => {
        const value = paramValues[p.name];
        if (value) {
            populatedPath = populatedPath.replace(`{${p.name}}`, encodeURIComponent(value));
        }
    });

    let php = `<?php\n\n`;
    php += `$curl = curl_init();\n\n`;
    
    let url = `${baseUrl}${populatedPath}`;
    
    const queryParams = parameters.filter(p => p.in === 'query');
    const queryParts = [];
    queryParams.forEach(p => {
        queryParts.push(encodeURIComponent(p.name) + '=' + encodeURIComponent(paramValues[p.name] || `your_${p.name}_value`));
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'query') {
        const schemeDetails = securitySchemes[activeScheme];
        const value = paramValues[schemeDetails.name];
        if (value) {
            queryParts.push(encodeURIComponent(schemeDetails.name) + '=' + encodeURIComponent(value));
        }
    }

    if (queryParts.length > 0) {
        url += `?` + queryParts.join('&');
    }

    php += `curl_setopt_array($curl, [\n`;
    php += `  CURLOPT_URL => "${url}",\n`;
    php += `  CURLOPT_RETURNTRANSFER => true,\n`;
    php += `  CURLOPT_ENCODING => "",\n`;
    php += `  CURLOPT_MAXREDIRS => 10,\n`;
    php += `  CURLOPT_TIMEOUT => 30,\n`;
    php += `  CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,\n`;
    php += `  CURLOPT_CUSTOMREQUEST => "${method.toUpperCase()}",\n`;

    if (requestBody) {
        let bodyData = { ...requestBody.example };
        parameters.filter(p => p.in === 'body').forEach(p => {
            if(paramValues.hasOwnProperty(p.name)) {
                bodyData[p.name] = paramValues[p.name];
            }
        });
        php += `  CURLOPT_POSTFIELDS => json_encode(${JSON.stringify(bodyData, null, 2)}),\n`;
    }

    let headers = `  CURLOPT_HTTPHEADER => [\n`;
    
    const headerParams = parameters.filter(p => p.in === 'header');
    headerParams.forEach(h => {
        headers += `    "${h.name}: ${paramValues[h.name] || `{${h.name}_value}`}",\n`;
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        headers += `    "${schemeDetails.name}: ${token}",\n`;
    }

    if (requestBody) {
        headers += `    "content-type: application/json"\n`;
    }
    
    php += headers + `  ],\n`;
    php += `]);\n\n`;
    php += `$response = curl_exec($curl);\n`;
    php += `$err = curl_error($curl);\n\n`;
    php += `curl_close($curl);\n\n`;
    php += `if ($err) {\n`;
    php += `  echo "cURL Error #:" . $err;\n`;
    php += `} else {\n`;
    php += `  echo $response;\n`;
    php += `}`;

    return php;
};

export const generateCSharpSnippet = (api, paramValues, securitySchemes, activeScheme) => {
    const { baseUrl, path, method, parameters, requestBody } = api;
    let csharp = `using System;\nusing System.Net.Http;\nusing System.Threading.Tasks;\n\n`;
    csharp += `class Program {\n`;
    csharp += `    static async Task Main(string[] args) {\n`;
    csharp += `        var client = new HttpClient();\n`;
    csharp += `        var request = new HttpRequestMessage(HttpMethod.${method.charAt(0).toUpperCase() + method.slice(1).toLowerCase()}, "${baseUrl}${path}");\n`;

    const headers = parameters.filter(p => p.in === 'header');
    headers.forEach(h => {
        csharp += `        request.Headers.Add("${h.name}", "${paramValues[h.name] || `{${h.name}_value}`}");\n`;
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        csharp += `        request.Headers.Add("${schemeDetails.name}", "${token}");\n`;
    }

    if (requestBody) {
        let bodyData = { ...requestBody.example };
        parameters.filter(p => p.in === 'body').forEach(p => {
            if(paramValues.hasOwnProperty(p.name)) {
                bodyData[p.name] = paramValues[p.name];
            }
        });
        csharp += `        var content = new StringContent(System.Text.Json.JsonSerializer.Serialize(${JSON.stringify(bodyData, null, 2)}), null, "application/json");\n`;
        csharp += `        request.Content = content;\n`;
    }
    
    csharp += `        var response = await client.SendAsync(request);\n`;
    csharp += `        response.EnsureSuccessStatusCode();\n`;
    csharp += `        Console.WriteLine(await response.Content.ReadAsStringAsync());\n`;
    csharp += `    }\n`;
    csharp += `}`;

    return csharp;
};

export const generateSwiftSnippet = (api, paramValues, securitySchemes, activeScheme) => {
    const { baseUrl, path, method, parameters, requestBody } = api;
    let swift = `import Foundation\n\n`;
    
    let urlString = `${baseUrl}${path}`;
    const queryParams = parameters.filter(p => p.in === 'query');
    const queryItems = [];
    queryParams.forEach(p => {
        queryItems.push(`URLQueryItem(name: "${p.name}", value: "${paramValues[p.name] || `your_${p.name}_value`}")`);
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'query') {
        const schemeDetails = securitySchemes[activeScheme];
        const value = paramValues[schemeDetails.name];
        if (value) {
            queryItems.push(`URLQueryItem(name: "${schemeDetails.name}", value: "${value}")`);
        }
    }

    if (queryItems.length > 0) {
        swift += `var components = URLComponents(string: "${urlString}")!\n`;
        swift += `components.queryItems = [\n    ` + queryItems.join(',\n    ') + `\n]\n`;
        swift += `let url = components.url!\n\n`;
    } else {
        swift += `let url = URL(string: "${urlString}")!\n\n`;
    }
    
    swift += `var request = URLRequest(url: url)\n`;
    swift += `request.httpMethod = "${method.toUpperCase()}"\n`;

    const headers = parameters.filter(p => p.in === 'header');
    headers.forEach(h => {
        swift += `request.addValue("${paramValues[h.name] || `{${h.name}_value}`}", forHTTPHeaderField: "${h.name}")\n`;
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        swift += `request.addValue("${token}", forHTTPHeaderField: "${schemeDetails.name}")\n`;
    }

    if (requestBody) {
        let bodyData = { ...requestBody.example };
        parameters.filter(p => p.in === 'body').forEach(p => {
            if(paramValues.hasOwnProperty(p.name)) {
                bodyData[p.name] = paramValues[p.name];
            }
        });
        swift += `let jsonData = try! JSONSerialization.data(withJSONObject: ${JSON.stringify(bodyData, null, 2)})\n`;
        swift += `request.httpBody = jsonData\n`;
        swift += `request.addValue("application/json", forHTTPHeaderField: "Content-Type")\n`;
    }

    swift += `\nlet task = URLSession.shared.dataTask(with: request) { data, response, error in \n`;
    swift += `  guard let data = data, error == nil else {\n`;
    swift += `    print(error?.localizedDescription ?? "No data")\n`;
    swift += `    return\n`;
    swift += `  }\n`;
    swift += `  let responseJSON = try? JSONSerialization.jsonObject(with: data, options: [])\n`;
    swift += `  if let responseJSON = responseJSON as? [String: Any] {\n`;
    swift += `    print(responseJSON)\n`;
    swift += `  }\n`;
    swift += `}\n\n`;
    swift += `task.resume()`;

    return swift;
};

export const generateHTTPSnippet = (api, paramValues, securitySchemes, activeScheme) => {
    const { baseUrl, path, method, parameters, requestBody } = api;
    let http = `${method.toUpperCase()} ${path}`;

    const queryParams = parameters.filter(p => p.in === 'query');
    if (queryParams.length > 0) {
        const queryParts = queryParams.map(p => `${p.name}=${paramValues[p.name] || `your_${p.name}_value`}`);
        http += `?` + queryParts.join('&');
    }
    
    http += ` HTTP/1.1\n`;
    http += `Host: ${new URL(baseUrl).hostname}\n`;
    
    const headers = parameters.filter(p => p.in === 'header');
    headers.forEach(h => {
        http += `${h.name}: ${paramValues[h.name] || `{${h.name}_value}`}\n`;
    });

    if (activeScheme && securitySchemes[activeScheme] && securitySchemes[activeScheme].type === 'apiKey' && securitySchemes[activeScheme].in === 'header') {
        const schemeDetails = securitySchemes[activeScheme];
        let token = paramValues[schemeDetails.name] || `{${schemeDetails.name}_value}`;
        if (schemeDetails.name === "authorization" && !token.startsWith("Bearer")) {
          token = `Bearer ${token}`;
        }
        http += `${schemeDetails.name}: ${token}\n`;
    }

    if (requestBody) {
        http += `Content-Type: application/json\n`;
        let bodyData = { ...requestBody.example };
        parameters.filter(p => p.in === 'body').forEach(p => {
            if(paramValues.hasOwnProperty(p.name)) {
                bodyData[p.name] = paramValues[p.name];
            }
        });
        http += `\n${JSON.stringify(bodyData, null, 2)}`;
    }

    return http;
};

const resolveRef = (spec, ref) => {
  const parts = ref.replace('#/', '').split('/');
  return parts.reduce((obj, part) => obj && obj[part], spec);
}

export const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

export const parseOpenApiSpec = (openApiSpec) => {
  const baseUrl = openApiSpec.servers && openApiSpec.servers[0] ? openApiSpec.servers[0].url : '';
  const securitySchemes = openApiSpec.components && openApiSpec.components.securitySchemes;
  const groupedApis = {};

  for (const path in openApiSpec.paths) {
    for (const method in openApiSpec.paths[path]) {
      const endpoint = openApiSpec.paths[path][method];
      
      let allParameters = [];
      if(endpoint.parameters) {
        allParameters = allParameters.concat(endpoint.parameters.map(p => {
          if (p.$ref) {
            return resolveRef(openApiSpec, p.$ref);
          }
          return p;
        }));
      }

      // Add security scheme parameters
      if (securitySchemes && endpoint.security) {
        endpoint.security.forEach(securityObj => {
          const schemeName = Object.keys(securityObj)[0];
          if (securitySchemes[schemeName] && securitySchemes[schemeName].type === 'apiKey') {
            allParameters.push({
              name: securitySchemes[schemeName].name,
              in: securitySchemes[schemeName].in,
              description: securitySchemes[schemeName].description || 'Authentication token',
              required: true,
              schema: { type: 'string' }
            });
          }
        });
      }

      let requestBody = null;
      if (endpoint.requestBody) {
          const rb = endpoint.requestBody.content && endpoint.requestBody.content['application/json'];
          if(rb && rb.schema) {
              const schema = rb.schema.$ref ? resolveRef(openApiSpec, rb.schema.$ref) : rb.schema;
              requestBody = {
                  description: endpoint.requestBody.description || '',
                  example: rb.example || schema.example || {}
              };

              // Add body parameters to the list for UI rendering
              if (schema.properties) {
                for (const propName in schema.properties) {
                    const prop = schema.properties[propName];
                    if (prop.type === 'array' && prop.items) {
                        const itemsSchema = prop.items.$ref ? resolveRef(openApiSpec, prop.items.$ref) : prop.items;
                        allParameters.push({
                            name: propName,
                            in: 'body',
                            description: prop.description || '',
                            required: (schema.required && schema.required.includes(propName)) || false,
                            schema: {
                                type: 'array',
                                items: itemsSchema
                            }
                        });
                    } else {
                        allParameters.push({
                            name: propName,
                            in: 'body',
                            description: prop.description || '',
                            required: (schema.required && schema.required.includes(propName)) || false,
                            schema: { type: prop.type || 'object' }
                        });
                    }
                }
              }
          }
      }
      
      const responses = {};
      for (const statusCode in endpoint.responses) {
          const responseDef = endpoint.responses[statusCode];
          const content = responseDef.content && responseDef.content['application/json'];
          let body = {};
          if (content) {
              if (content.example) {
                  body = content.example;
              } else if (content.schema) {
                  const schema = content.schema.$ref ? resolveRef(openApiSpec, content.schema.$ref) : content.schema;
                  body = schema.example || { message: responseDef.description };
              }
          }
          responses[statusCode] = {
              statusCode: parseInt(statusCode),
              description: responseDef.description,
              body: body,
          };
      }


      let description = endpoint.description || '';
      let requestSample = null;
      let responseSample = null;

      const sampleRegex = /<pre><code.*?>([\s\S]*?)<\/code><\/pre>/g;
      let cleanedDescription = description;
      const embeddedSamples = [];
      
      let match;
      while ((match = sampleRegex.exec(description)) !== null) {
          const codeContent = match[1].replace(/"/g, '"').trim();
          try {
              const parsedJson = JSON.parse(codeContent);
              // It's a valid JSON object, let's extract it
              embeddedSamples.push(parsedJson);
              // Mark the matched part for removal
              cleanedDescription = cleanedDescription.replace(match[0], '');
          } catch (e) {
              // It's not a JSON object, so we leave it in the description
              // It will be rendered as a regular code block.
          }
      }
      
      const api = {
        id: endpoint.operationId || `${method}-${path.replace(/\//g, '-')}`,
        slug: slugify(endpoint.summary),
        name: endpoint.summary || 'No Summary',
        description: cleanedDescription.trim(),
        embeddedSamples: embeddedSamples, // New property
        method: method.toUpperCase(),
        path: path,
        parameters: allParameters || [],
        responses: responses,
        requestSample: requestSample, // This can likely be deprecated later
        responseSample: responseSample, // This can likely be deprecated later
        examples: {},
        security: endpoint.security || openApiSpec.security,
        baseUrl: endpoint['x-server-url']
      };
      
      api.requestBody = requestBody;

      // Storing a template for initial display. Dynamic snippets will be generated in RightPanel.
      api.examples = {
        curl: generateCurlSnippet(api, {}, securitySchemes, null, true),
        javascript: generateJavascriptSnippet(api, {}, securitySchemes, null, true),
        python: generatePythonSnippet(api, {}, securitySchemes, null, true),
        ruby: generateRubySnippet(api, {}, securitySchemes, null, true),
        c: generateCSnippet(api, {}, securitySchemes, null, true),
        cpp: generateCppSnippet(api, {}, securitySchemes, null, true),
        java: generateJavaSnippet(api, {}, securitySchemes, null, true),
        shell: generateShellSnippet(api, {}, securitySchemes, null, true),
        node: generateNodeSnippet(api, {}, securitySchemes, null, true),
        php: generatePHPSnippet(api, {}, securitySchemes, null, true),
        csharp: generateCSharpSnippet(api, {}, securitySchemes, null, true),
        swift: generateSwiftSnippet(api, {}, securitySchemes, null, true),
        http: generateHTTPSnippet(api, {}, securitySchemes, null, true),
      };

      const tagName = endpoint.tags && endpoint.tags.length > 0 ? endpoint.tags[0] : 'default';
      if (!groupedApis[tagName]) {
        groupedApis[tagName] = [];
      }
      groupedApis[tagName].push(api);
    }
  }

  const finalApis = {};

  for (const path in openApiSpec.paths) {
    for (const method in openApiSpec.paths[path]) {
      const endpoint = openApiSpec.paths[path][method];
      const mainGroupName = endpoint['x-main-group'] || 'Partner Endpoints';
      const subGroupName = endpoint.tags && endpoint.tags.length > 0 ? endpoint.tags[0] : 'default';

      if (!finalApis[mainGroupName]) {
        finalApis[mainGroupName] = {
          slug: slugify(mainGroupName),
          groups: {}
        };
      }
      if (!finalApis[mainGroupName].groups[subGroupName]) {
        finalApis[mainGroupName].groups[subGroupName] = {
          name: subGroupName,
          slug: slugify(subGroupName),
          apis: []
        };
      }
      const api = groupedApis[subGroupName] && groupedApis[subGroupName].find(a => a.path === path && a.method === method.toUpperCase());
      if(api) {
        finalApis[mainGroupName].groups[subGroupName].apis.push(api);
      }
    }
  }

  return { apis: finalApis, baseUrl: baseUrl, securitySchemes: securitySchemes };
};